<template>
  <!-- Add / Edit Policy -->
  <div class="modal fade dq-modal" id="add_policy" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg dq-modal-lg">
      <div class="modal-content dq-modal-card">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="d-flex align-items-center gap-3">
            <span class="dq-icon-badge dq-icon-policy">
              <i class="ti ti-file-description"></i>
            </span>

            <div class="lh-1">
              <h4 class="modal-title mb-1">
                {{ isEdit ? "Edit Policy" : "Add Policy" }}
              </h4>
              <div class="dq-subtitle">
                Upload a policy document and optionally scope it to a department.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn dq-close-btn"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitForm" novalidate ref="formRef">
          <!-- Body -->
          <div class="modal-body dq-modal-body">
            <div class="row">
              <!-- Policy Name -->
              <div class="col-md-12">
                <div class="mb-3 dq-field">
                  <label class="form-label dq-label">
                    Policy Name <span class="text-danger">*</span>
                  </label>

                  <div class="dq-input-wrap">
                    <span class="dq-input-icon"><i class="ti ti-id"></i></span>
                    <input
                      v-model="form.name"
                      type="text"
                      class="form-control dq-input"
                      required
                      placeholder="e.g. Leave Policy 2026"
                      autocomplete="off"
                    />
                  </div>

                  <div class="dq-help">
                    Keep it clear and searchable. Example: “Code of Conduct — v2”.
                  </div>
                </div>
              </div>

              <!-- Description -->
              <div class="col-md-12">
                <div class="mb-3 dq-field">
                  <label class="form-label dq-label">Description</label>

                  <div class="dq-textarea-wrap">
                    <span class="dq-input-icon dq-textarea-icon">
                      <i class="ti ti-align-left"></i>
                    </span>
                    <textarea
                      v-model="form.description"
                      class="form-control dq-textarea"
                      rows="4"
                      placeholder="Optional summary: what this policy covers, who it applies to, effective date, etc."
                    ></textarea>
                  </div>

                  <div class="dq-help">
                    This helps employees understand the document before downloading.
                  </div>
                </div>
              </div>

              <!-- Department -->
              <div class="col-md-12">
                <div class="mb-3 dq-field">
                  <label class="form-label dq-label">Department</label>

                  <div class="dq-select-wrap">
                    <span class="dq-input-icon"><i class="ti ti-building-community"></i></span>
                    <vue-select
                      v-model="form.department_id"
                      :options="departmentOptions"
                      label="text"
                      track-by="id"
                      placeholder="Select Department"
                      class="dq-vselect"
                      :clearable="true"
                      :reduce="(o) => o.id"
                    />
                  </div>

                  <div class="dq-help">
                    Leave blank to apply to all departments.
                  </div>
                </div>
              </div>

              <!-- Upload Policy -->
              <div class="col-md-12">
                <div class="mb-3 dq-field">
                  <label class="form-label dq-label">Upload Policy</label>

                  <div class="dq-dropzone">
                    <div class="dq-dropzone-left">
                      <div class="dq-dropzone-icon">
                        <img
                          src="@/assets/img/folder-open.svg"
                          alt="Folder"
                          class="img-fluid"
                          style="width: 22px; height: 22px"
                        />
                      </div>

                      <div>
                        <div class="dq-dropzone-title">
                          Drag & drop your file here, or upload
                        </div>
                        <div class="dq-help">
                          PDF is recommended for best viewing. Replace the file anytime.
                        </div>
                      </div>
                    </div>

                    <div class="dq-dropzone-actions">
                      <label class="btn btn-primary dq-btn-primary btn-sm dq-upload-btn mb-0">
                        <i class="ti ti-upload me-1"></i>
                        Upload
                        <input
                          type="file"
                          class="dq-file-input"
                          @change="onFileChange"
                        />
                      </label>
                    </div>
                  </div>

                  <!-- File meta -->
                  <div class="dq-filemeta mt-2">
                    <div v-if="currentFileName" class="dq-filemeta-row">
                      <span class="dq-filemeta-badge dq-badge-new">Selected</span>
                      <span class="dq-filemeta-name">{{ currentFileName }}</span>
                    </div>

                    <div v-else-if="existingFileName" class="dq-filemeta-row">
                      <span class="dq-filemeta-badge dq-badge-muted">Current</span>
                      <span class="dq-filemeta-name">{{ existingFileName }}</span>
                    </div>

                    <div v-else class="dq-help">
                      No file selected yet.
                    </div>
                  </div>

                  <div class="dq-info-strip mt-3">
                    <i class="ti ti-shield-check me-2"></i>
                    Your document will be stored securely and visible based on department scope.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sticky Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-white border dq-btn-soft me-2"
              data-bs-dismiss="modal"
              :disabled="submitting"
            >
              Cancel
            </button>

            <button type="submit" class="btn btn-primary dq-btn-primary" :disabled="submitting">
              <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
              <span v-if="submitting">Saving…</span>
              <span v-else>{{ isEdit ? "Save Changes" : "Add Policy" }}</span>
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- /Add / Edit Policy -->

  <!-- Delete Modal -->
  <div class="modal fade dq-modal" id="delete_modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-card">
        <div class="modal-body dq-delete-body">
          <div class="dq-danger-hero">
            <span class="dq-danger-badge">
              <i class="ti ti-trash-x"></i>
            </span>
          </div>

          <h4 class="mb-1 dq-delete-title">Confirm delete</h4>
          <p class="mb-3 dq-delete-subtitle">
            Are you sure you want to delete policy
            <b>{{ policyToDelete?.name }}</b>? This cannot be undone.
          </p>

          <div class="dq-delete-warning">
            <i class="ti ti-alert-triangle me-2"></i>
            Tip: If you want to replace the policy, edit it and upload a new document instead.
          </div>

          <div class="d-flex justify-content-center gap-2 mt-4">
            <button
              type="button"
              class="btn btn-light dq-btn-soft"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>

            <button type="button" class="btn btn-danger dq-btn-danger" @click="confirmDelete">
              Yes, Delete
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- /Delete Modal -->
</template>

<script setup>
import { reactive, ref, computed, watch, onMounted } from "vue";
import { usePolicyStore } from "@/stores/policyStore";
import { useDepartmentStore } from "@/stores/departmentStore";
import { storeToRefs } from "pinia";
import { toast } from "@/utils/toast";

const props = defineProps({
  policy: { type: Object, default: null },
  policyToDelete: { type: Object, default: null },
});

const emit = defineEmits(["refresh"]);

const policyStore = usePolicyStore();
const departmentStore = useDepartmentStore();
const { departments } = storeToRefs(departmentStore);

const formRef = ref(null);
const form = reactive({
  name: "",
  description: "",
  department_id: null,
  file: null,
});

const submitting = ref(false);
const currentFileName = ref("");
const existingFileName = ref("");

const isEdit = computed(() => !!(props.policy && props.policy.id));

const departmentOptions = computed(() => [
  { id: null, name: "All Department" },
  ...departments.value.map((d) => ({ id: d.id, text: d.name })),
]);

function resetForm() {
  form.name = "";
  form.description = "";
  form.department_id = null;
  form.file = null;
  currentFileName.value = "";
  existingFileName.value = "";
}

watch(
  () => props.policy,
  (p) => {
    resetForm();
    if (p) {
      form.name = p.name || "";
      form.description = p.description || "";
      form.department_id = p.department_id || null;
      existingFileName.value = p.file_path ? p.file_path.split("/").slice(-1)[0] : "";
    }
  },
  { immediate: true }
);

function onFileChange(e) {
  const file = e.target.files[0];
  if (!file) return;
  form.file = file;
  currentFileName.value = file.name;
}

function closeModal(id) {
  const modalEl = document.getElementById(id);
  if (modalEl) {
    modalEl.classList.remove("show");
    modalEl.style.display = "none";
    modalEl.setAttribute("aria-hidden", "true");
    modalEl.removeAttribute("aria-modal");
  }
  document.body.classList.remove("modal-open");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

async function submitForm() {
  if (formRef.value && !formRef.value.checkValidity()) {
    formRef.value.reportValidity();
    return;
  }

  if (!form.name) {
    toast("Policy name is required.", "error");
    return;
  }

  submitting.value = true;

  const fd = new FormData();
  fd.append("name", form.name);
  if (form.description) fd.append("description", form.description);
  if (form.department_id) fd.append("department_id", form.department_id);
  if (form.file instanceof File) fd.append("file", form.file);

  try {
    if (isEdit.value) {
      fd.append("_method", "PUT");
      await policyStore.updatePolicy(props.policy.id, fd);
    } else {
      await policyStore.createPolicy(fd);
    }
    emit("refresh");
    setTimeout(() => closeModal("add_policy"), 100);
  } catch (err) {
    // errors already toasted in store
  } finally {
    submitting.value = false;
  }
}

async function confirmDelete() {
  if (!props.policyToDelete || !props.policyToDelete.id) return;
  try {
    await policyStore.deletePolicy(props.policyToDelete.id);
    emit("refresh");
    closeModal("delete_modal");
  } catch (err) {
    // handled in store
  }
}

onMounted(() => {
  if (!departments.value.length) {
    departmentStore.fetchDepartments();
  }
});
</script>

<style scoped>
/* ===== Premium modal polish (UI only) ===== */
.dq-modal-card {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 22px 60px rgba(16, 24, 40, 0.18);
}

.dq-modal-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 16px 18px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(255, 255, 255, 1));
}

.dq-subtitle {
  font-size: 0.85rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}

.dq-close-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.dq-close-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.08);
}

.dq-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
}
.dq-icon-policy {
  background: rgba(99, 102, 241, 0.12);
  color: rgba(79, 70, 229, 1);
}

/* Body */
.dq-modal-body {
  padding: 16px 18px;
  max-height: 70vh;
  overflow: auto;
}

/* Labels */
.dq-label {
  font-weight: 800;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 6px;
}

/* Inputs */
.dq-input-wrap,
.dq-select-wrap,
.dq-textarea-wrap {
  position: relative;
}
.dq-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(17, 24, 39, 0.45);
  z-index: 3;
  pointer-events: none;
}
.dq-textarea-icon {
  top: 18px;
  transform: none;
}
.dq-input {
  padding-left: 40px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-input:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12) !important;
}
.dq-textarea {
  padding-left: 40px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-textarea:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12) !important;
}

.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 6px;
}

/* vue-select */
.dq-vselect :deep(.vs__dropdown-toggle) {
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding-left: 40px;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-vselect :deep(.vs__dropdown-toggle:focus-within) {
  border-color: rgba(59, 130, 246, 0.45);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12);
}
.dq-vselect :deep(.vs__dropdown-menu) {
  border-radius: 14px;
  padding: 6px;
  box-shadow: 0 18px 40px rgba(16, 24, 40, 0.16);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

/* Dropzone */
.dq-dropzone {
  border-radius: 16px;
  padding: 14px;
  border: 1px dashed rgba(148, 163, 184, 0.55);
  background: rgba(148, 163, 184, 0.10);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.dq-dropzone-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 240px;
}
.dq-dropzone-icon {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  background: rgba(59, 130, 246, 0.12);
  border: 1px solid rgba(59, 130, 246, 0.14);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.dq-dropzone-title {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.85);
}

/* Upload button */
.dq-upload-btn {
  position: relative;
  overflow: hidden;
}
.dq-file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

/* File meta */
.dq-filemeta-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
}
.dq-filemeta-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 0.72rem;
}
.dq-badge-new {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}
.dq-badge-muted {
  background: rgba(148, 163, 184, 0.18);
  color: rgba(71, 85, 105, 1);
}
.dq-filemeta-name {
  font-weight: 700;
  color: rgba(17, 24, 39, 0.78);
}

/* Info strip */
.dq-info-strip {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(2, 132, 199, 0.08);
  border: 1px solid rgba(2, 132, 199, 0.12);
  color: rgba(17, 24, 39, 0.65);
  font-size: 0.82rem;
  font-weight: 700;
}

/* Sticky footer */
.dq-modal-footer {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 14px 18px;
}

.dq-btn-soft {
  border-radius: 12px;
  padding: 10px 14px;
}
.dq-btn-primary {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(37, 99, 235, 0.18);
}

/* Delete modal */
.dq-delete-body {
  padding: 18px;
  text-align: center;
}
.dq-danger-hero {
  display: flex;
  justify-content: center;
  margin-top: 6px;
}
.dq-danger-badge {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.12);
  color: rgba(220, 38, 38, 1);
  font-size: 26px;
}
.dq-delete-title {
  font-weight: 900;
  letter-spacing: -0.02em;
}
.dq-delete-subtitle {
  color: rgba(17, 24, 39, 0.65);
  font-size: 0.92rem;
  line-height: 1.35rem;
}
.dq-delete-warning {
  text-align: left;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(245, 158, 11, 0.10);
  border: 1px solid rgba(245, 158, 11, 0.14);
  color: rgba(120, 53, 15, 0.95);
  font-size: 0.84rem;
}
.dq-btn-danger {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(220, 38, 38, 0.14);
}
</style>
